import { Auth } from "@aws-amplify/auth";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";
import accountReducer from "store/accountReducer";
import {
  END_REGISTER,
  LOGIN,
  LOGOUT,
  NEW_PASSWORD_REQUIRED,
} from "store/actions";
import Loader from "ui-component/Loader";
// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  isRegistrationNeeded: false,
  user: null,
};

const _mapUserFromCognitoUser = (cognitoUser) => {
  try {
    return {
      id: cognitoUser.attributes.sub,
      email: cognitoUser.attributes.email,
    };
  } catch (err) {
    console.log(err);
    throw err;
  }
};

const _mapAWSErrorCode = (error) => {
  if (Object.keys(AWS_CODE_VERIFICATION_CODE).includes(error.code)) {
    return AWS_CODE_VERIFICATION_CODE[error.code];
  }
  return AWS_CODE_VERIFICATION_CODE["DEFAULT"];
};

const AWS_CODE_VERIFICATION_CODE = {
  ExpiredCodeException: "le code a expiré",
  LimitExceededException:
    "Trop de requetes, merci d'attendre avant de réessayer",
  CodeMismatchException: "Mauvais code",
  DEFAULT: "Erreur inconnue, Veuillez reessayer.",
};

// ==============================|| AWS Cognito CONTEXT & PROVIDER ||============================== //
const AWSCognitoContext = createContext(null);

export const AWSCognitoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const init = async () => {
    try {
      Auth.currentAuthenticatedUser()
        .then((user) => {
          console.log(user);
          const mappedUser = _mapUserFromCognitoUser(user);
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: mappedUser,
            },
          });
          Sentry.setContext({
            email: mappedUser.email,
            id: user.username,
          });
        })
        .catch((err) => {
          console.log(err);
          logout();
        });
    } catch (err) {
      console.log(err);
      logout();
    }
  };

  useEffect(() => {
    init();
  }, []);

  const login = async (email, password) => {
    return Auth.signIn(email, password)
      .then((user) => {
        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
          dispatch({
            type: NEW_PASSWORD_REQUIRED,
            payload: {
              isLoggedIn: true,
              user: user,
            },
          });
        } else {
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: _mapUserFromCognitoUser(user),
            },
          });
        }
        return Promise.resolve();
      })
      .catch((err) => {
        console.log("Dans mon catch");
        console.error(err);
        return Promise.reject(err);
      });
  };

  const register = (user, password, requiredAttributes) =>
    new Promise((success, rej) => {
      return Auth.completeNewPassword(state.user, password, {
        given_name: "given_name",
        family_name: "family_name",
        gender: "male",
        address: JSON.stringify({
          street_address: "street_address",
          locality: "locality",
          postal_code: "postal_code",
          country: "country",
          formatted: `formatted`,
        }),
      })
        .then((result) => {
          init();
          dispatch({ type: END_REGISTER });
          success(result);
        })
        .catch((err) => {
          console.log(err);
          rej(err);
        });
    });

  const logout = async () => {
    try {
      await Auth.signOut();
    } catch (err) {
      console.error(err);
    } finally {
      dispatch({ type: LOGOUT });
    }
  };

  const resetPassword = (email) => {
    return Auth.forgotPassword(email);
  };

  const resetPassword2 = (email, code, newPassword) => {
    return Auth.forgotPasswordSubmit(email, code, newPassword)
      .then((ok) => Promise.resolve())
      .catch((err) => Promise.reject(_mapAWSErrorCode(err)));
  };

  const changePassword = (oldPassword, newPassword) => {
    return Auth.currentAuthenticatedUser()
      .then((user) => {
        return Auth.changePassword(user, oldPassword, newPassword)
          .then((e) => {
            return logout();
          })
          .catch((err) => Promise.reject(err));
      })
      .catch((err) => Promise.reject(err));
  };

  const getUserLetters = () => {
    if (state.user && state.user.name) {
      return `${state.user.name.toUpperCase().substring(0, 2)}`;
    }
    return "";
  };

  const askForRegistration = (
    firstName,
    lastName,
    email,
    phone,
    captchaValue
  ) => {
    return axios.post("https://api.sportpxl.com/contact-form", {
      firstName,
      lastName,
      email,
      phone,
      token: captchaValue,
    });
  };

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  return (
    <AWSCognitoContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        resetPassword2,
        getUserLetters,
        changePassword,
        askForRegistration,
      }}
    >
      {children}
    </AWSCognitoContext.Provider>
  );
};

AWSCognitoProvider.propTypes = {
  children: PropTypes.node,
};

export default AWSCognitoContext;
